/**
 * Smart Circle starter wheel library: famous people's daily routines and
 * popular productivity systems. Schedules are sourced from interviews,
 * biographies, and well-documented routines where possible. When only
 * habits are known (not exact times) we extrapolate a reasonable wheel
 * that fits their stated rhythm.
 *
 * Each entry: { id, name, category, color, blocks: [{ startTime, endTime,
 * slotType, label, color }] }. All wheels cover 24h with no overlaps.
 */

const C = {
  sleep: '#6B46C1',
  work: '#D4663A',
  deep: '#B45309',
  meet: '#DC2626',
  food: '#A8BF8C',
  health: '#10B981',
  commute: '#06B6D4',
  chores: '#3B82F6',
  fun: '#F59E0B',
  mind: '#14B8A6',
  family: '#C7BEDD',
  social: '#F2C4A6',
  down: '#78716C',
  read: '#0EA5E9',
  write: '#7C3AED',
  create: '#EC4899',
};

// Helper to keep blocks readable.
const b = (startTime, endTime, slotType, label, color) => ({
  startTime, endTime, slotType, label, color,
});

export const FAMOUS_WHEELS = [
  // ─────────────────────────── HISTORICAL ───────────────────────────
  {
    id: 'famous_franklin',
    name: "Benjamin Franklin",
    category: 'Historical',
    color: '#B45309',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '08:00', 'mind', 'Rise, Plan, Breakfast', C.mind),
      b('08:00', '12:00', 'work', 'Work', C.work),
      b('12:00', '14:00', 'food', 'Read & Lunch', C.food),
      b('14:00', '18:00', 'work', 'Work', C.work),
      b('18:00', '22:00', 'fun_hobby', 'Music, Conversation, Review', C.fun),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_darwin',
    name: "Charles Darwin",
    category: 'Historical',
    color: '#10B981',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '07:45', 'health', 'Walk & Breakfast', C.health),
      b('07:45', '09:30', 'work', 'Best Work', C.deep),
      b('09:30', '10:30', 'mind', 'Letters', C.mind),
      b('10:30', '12:00', 'work', 'Second Work', C.deep),
      b('12:00', '13:00', 'health', 'Walk on the Sandwalk', C.health),
      b('13:00', '15:00', 'food', 'Lunch & Newspaper', C.food),
      b('15:00', '16:00', 'downtime', 'Rest', C.down),
      b('16:00', '17:30', 'health', 'Walk', C.health),
      b('17:30', '18:00', 'work', 'Light Work', C.work),
      b('18:00', '22:30', 'family', 'Family & Reading', C.family),
      b('22:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_napoleon',
    name: "Napoleon Bonaparte",
    category: 'Historical',
    color: '#7C3AED',
    blocks: [
      b('00:00', '04:00', 'sleep', 'Sleep', C.sleep),
      b('04:00', '06:00', 'work', 'Dispatches & Maps', C.deep),
      b('06:00', '07:00', 'health', 'Bath & Briefing', C.health),
      b('07:00', '12:00', 'work', 'Council & Decisions', C.work),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '18:00', 'work', 'Field & Strategy', C.work),
      b('18:00', '20:00', 'food', 'Dinner', C.food),
      b('20:00', '23:00', 'mind', 'Reading & Letters', C.mind),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_beethoven',
    name: "Ludwig van Beethoven",
    category: 'Historical',
    color: '#B45309',
    blocks: [
      b('00:00', '05:30', 'sleep', 'Sleep', C.sleep),
      b('05:30', '06:30', 'food', 'Coffee (60 beans, counted)', C.food),
      b('06:30', '14:30', 'fun_hobby', 'Compose', C.create),
      b('14:30', '15:30', 'food', 'Lunch', C.food),
      b('15:30', '17:30', 'health', 'Long Walk', C.health),
      b('17:30', '20:00', 'social', 'Tavern & Newspaper', C.social),
      b('20:00', '22:00', 'fun_hobby', 'Light Composing', C.create),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_mozart',
    name: "Wolfgang Amadeus Mozart",
    category: 'Historical',
    color: '#EC4899',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'mind', 'Rise & Dress', C.mind),
      b('07:00', '09:00', 'fun_hobby', 'Compose', C.create),
      b('09:00', '13:00', 'work', 'Teach Lessons', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '17:00', 'social', 'Visits & Performances', C.social),
      b('17:00', '21:00', 'social', 'Concert / Dinner', C.social),
      b('21:00', '01:00', 'fun_hobby', 'Compose Late', C.create),
      // wraps overnight: handled as 21-23:59 in storage; covered by next wheel's morning sleep
    ],
  },
  {
    id: 'famous_kafka',
    name: "Franz Kafka",
    category: 'Historical',
    color: '#1E293B',
    blocks: [
      b('00:00', '03:00', 'mind', 'Writing', C.write),
      b('03:00', '08:30', 'sleep', 'Sleep', C.sleep),
      b('08:30', '14:30', 'work', 'Day Job', C.work),
      b('14:30', '15:30', 'food', 'Lunch', C.food),
      b('15:30', '19:30', 'downtime', 'Nap & Rest', C.down),
      b('19:30', '21:00', 'health', 'Walk & Dinner', C.health),
      b('21:00', '23:00', 'family', 'Family Time', C.family),
      b('23:00', '00:00', 'mind', 'Begin Writing', C.write),
    ],
  },
  {
    id: 'famous_dickens',
    name: "Charles Dickens",
    category: 'Historical',
    color: '#7C3AED',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '14:00', 'mind', 'Writing (Strict)', C.write),
      b('14:00', '15:00', 'food', 'Lunch', C.food),
      b('15:00', '17:30', 'health', '3-Hour Walk', C.health),
      b('17:30', '19:00', 'family', 'Family Time', C.family),
      b('19:00', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '00:00', 'social', 'Reading & Society', C.social),
    ],
  },
  {
    id: 'famous_hugo',
    name: "Victor Hugo",
    category: 'Historical',
    color: '#06B6D4',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '11:00', 'mind', 'Writing', C.write),
      b('11:00', '12:00', 'health', 'Ice Bath on Roof', C.health),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'work', 'Correspondence & Edits', C.work),
      b('17:00', '19:00', 'health', 'Walk', C.health),
      b('19:00', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '00:00', 'social', 'Visits & Reading', C.social),
    ],
  },
  {
    id: 'famous_hemingway',
    name: "Ernest Hemingway",
    category: 'Historical',
    color: '#A8BF8C',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '12:00', 'mind', 'Writing (Standing)', C.write),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'fun_hobby', 'Fishing / Outdoors', C.fun),
      b('17:00', '19:00', 'social', 'Drinks', C.social),
      b('19:00', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '23:00', 'mind', 'Reading', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_einstein',
    name: "Albert Einstein",
    category: 'Historical',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep (10h)', C.sleep),
      b('07:00', '08:00', 'food', 'Breakfast & Newspaper', C.food),
      b('08:00', '10:00', 'commuting_travel', 'Walk to Princeton', C.commute),
      b('10:00', '13:00', 'work', 'Office Hours', C.deep),
      b('13:00', '14:30', 'food', 'Lunch', C.food),
      b('14:30', '17:00', 'work', 'Research', C.deep),
      b('17:00', '19:00', 'health', 'Walk Home', C.health),
      b('19:00', '21:00', 'food', 'Dinner & Music', C.food),
      b('21:00', '00:00', 'mind', 'Read & Reflect', C.mind),
    ],
  },
  {
    id: 'famous_davinci',
    name: "Leonardo da Vinci",
    category: 'Historical',
    color: '#F59E0B',
    blocks: [
      b('00:00', '02:00', 'sleep', 'Nap', C.sleep),
      b('02:00', '02:20', 'fun_hobby', 'Sketch', C.create),
      b('02:20', '06:00', 'sleep', 'Nap', C.sleep),
      b('06:00', '06:20', 'fun_hobby', 'Sketch', C.create),
      b('06:20', '10:00', 'sleep', 'Nap', C.sleep),
      b('10:00', '12:00', 'fun_hobby', 'Studio Work', C.create),
      b('12:00', '13:00', 'food', 'Light Meal', C.food),
      b('13:00', '17:00', 'fun_hobby', 'Painting & Anatomy', C.create),
      b('17:00', '19:00', 'mind', 'Notebooks', C.mind),
      b('19:00', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '23:30', 'fun_hobby', 'Drafting', C.create),
      b('23:30', '00:00', 'sleep', 'Nap', C.sleep),
    ],
  },
  {
    id: 'famous_kant',
    name: "Immanuel Kant",
    category: 'Historical',
    color: '#3B82F6',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '07:00', 'mind', 'Tea & Pipe & Reading', C.mind),
      b('07:00', '11:00', 'work', 'Lecture & Writing', C.work),
      b('11:00', '13:00', 'social', 'Lunch with Guests', C.social),
      b('13:00', '15:30', 'health', 'Daily Walk (Königsberg)', C.health),
      b('15:30', '19:00', 'mind', 'Reading', C.read),
      b('19:00', '22:00', 'mind', 'Writing', C.write),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_edison',
    name: "Thomas Edison",
    category: 'Historical',
    color: '#F59E0B',
    blocks: [
      b('00:00', '04:00', 'sleep', 'Short Sleep', C.sleep),
      b('04:00', '05:00', 'fun_hobby', 'Lab Notes', C.create),
      b('05:00', '08:00', 'food', 'Coffee & Plan', C.food),
      b('08:00', '12:00', 'work', 'Lab Work', C.deep),
      b('12:00', '12:30', 'downtime', 'Power Nap', C.down),
      b('12:30', '17:00', 'work', 'Lab Work', C.deep),
      b('17:00', '18:00', 'food', 'Dinner', C.food),
      b('18:00', '23:00', 'fun_hobby', 'Late Lab Work', C.create),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_tesla',
    name: "Nikola Tesla",
    category: 'Historical',
    color: '#06B6D4',
    blocks: [
      b('00:00', '02:00', 'fun_hobby', 'Lab Experiments', C.create),
      b('02:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '08:00', 'health', 'Walk & Toe Exercises', C.health),
      b('08:00', '12:00', 'work', 'Office', C.work),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '20:00', 'work', 'Lab', C.deep),
      b('20:00', '22:00', 'social', 'Delmonicos Dinner', C.social),
      b('22:00', '00:00', 'fun_hobby', 'Late Lab', C.create),
    ],
  },
  {
    id: 'famous_lincoln',
    name: "Abraham Lincoln",
    category: 'Historical',
    color: '#1E40AF',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'mind', 'Newspaper & Coffee', C.read),
      b('07:00', '09:00', 'food', 'Breakfast & Cabinet', C.food),
      b('09:00', '12:00', 'work', 'Office Hours', C.work),
      b('12:00', '13:00', 'food', 'Lunch (Light)', C.food),
      b('13:00', '17:00', 'work', 'Cabinet Meetings', C.work),
      b('17:00', '19:00', 'health', 'Carriage Ride', C.health),
      b('19:00', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '23:00', 'mind', 'Telegraph Office', C.mind),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },

  // ─────────────────────────── TECH / CEOs ───────────────────────────
  {
    id: 'famous_jobs',
    name: "Steve Jobs",
    category: 'Tech & CEOs',
    color: '#000000',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:30', 'mind', 'Mirror Question, Walk', C.mind),
      b('07:30', '08:00', 'food', 'Smoothie Breakfast', C.food),
      b('08:00', '12:00', 'work', 'Walking 1:1s', C.meet),
      b('12:00', '13:00', 'food', 'Lunch (Salad)', C.food),
      b('13:00', '18:00', 'work', 'Product Reviews', C.deep),
      b('18:00', '19:30', 'family', 'Family Dinner', C.family),
      b('19:30', '23:00', 'work', 'Email & Strategy', C.work),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_cook',
    name: "Tim Cook",
    category: 'Tech & CEOs',
    color: '#3B82F6',
    blocks: [
      b('00:00', '03:45', 'sleep', 'Sleep', C.sleep),
      b('03:45', '05:00', 'work', 'Email Triage (700+)', C.work),
      b('05:00', '06:00', 'health', 'Gym', C.health),
      b('06:00', '07:00', 'food', 'Breakfast', C.food),
      b('07:00', '12:00', 'work', 'Operations Reviews', C.meet),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '18:30', 'work', 'Meetings', C.work),
      b('18:30', '20:00', 'food', 'Dinner', C.food),
      b('20:00', '21:30', 'work', 'Late Email', C.work),
      b('21:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_bezos',
    name: "Jeff Bezos",
    category: 'Tech & CEOs',
    color: '#FF9900',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep (8h)', C.sleep),
      b('07:00', '09:00', 'family', 'Slow Mornings, Coffee', C.family),
      b('09:00', '10:00', 'food', 'Breakfast', C.food),
      b('10:00', '12:00', 'work', 'High-IQ Decisions Only', C.deep),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'work', 'Six-Pager Meetings', C.meet),
      b('17:00', '19:00', 'family', 'Dinner with Kids', C.family),
      b('19:00', '22:00', 'fun_hobby', 'Reading / Blue Origin', C.fun),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_musk',
    name: "Elon Musk (5-min Blocks)",
    category: 'Tech & CEOs',
    color: '#DC2626',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep (6h)', C.sleep),
      b('07:00', '07:30', 'mind', 'Email & Coffee', C.mind),
      b('07:30', '12:00', 'work', 'Engineering at Tesla/SpaceX', C.deep),
      b('12:00', '12:30', 'food', 'Lunch (at desk)', C.food),
      b('12:30', '17:00', 'work', 'Walks-and-Talks', C.meet),
      b('17:00', '20:00', 'work', 'Design Reviews', C.work),
      b('20:00', '21:00', 'family', 'Kids', C.family),
      b('21:00', '01:00', 'work', 'Late Email & Twitter', C.work),
    ],
  },
  {
    id: 'famous_gates',
    name: "Bill Gates",
    category: 'Tech & CEOs',
    color: '#10B981',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep (7h)', C.sleep),
      b('07:00', '08:00', 'health', 'Treadmill + Lecture', C.health),
      b('08:00', '09:00', 'food', 'Breakfast', C.food),
      b('09:00', '12:30', 'work', 'Foundation Meetings', C.meet),
      b('12:30', '13:30', 'food', 'Cheeseburger Lunch', C.food),
      b('13:30', '17:30', 'work', 'Letters & Strategy', C.work),
      b('17:30', '19:00', 'family', 'Family Dinner', C.family),
      b('19:00', '23:00', 'mind', 'Reading (1hr+ /night)', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_zuck',
    name: "Mark Zuckerberg",
    category: 'Tech & CEOs',
    color: '#1877F2',
    blocks: [
      b('00:00', '08:00', 'sleep', 'Sleep', C.sleep),
      b('08:00', '08:30', 'health', 'MMA / Workout', C.health),
      b('08:30', '09:00', 'food', 'Breakfast', C.food),
      b('09:00', '12:00', 'work', 'Meta Sync', C.meet),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '18:00', 'work', 'Decisions & Builds', C.work),
      b('18:00', '20:00', 'family', 'Family', C.family),
      b('20:00', '23:00', 'fun_hobby', 'Reading / Code', C.fun),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_dorsey',
    name: "Jack Dorsey",
    category: 'Tech & CEOs',
    color: '#000000',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '06:00', 'mind', 'Meditation (30 min)', C.mind),
      b('06:00', '07:00', 'health', '7-Min Workouts ×3', C.health),
      b('07:00', '07:30', 'food', 'Salt Juice (No Breakfast)', C.food),
      b('07:30', '08:30', 'commuting_travel', 'Walk to Office (5mi)', C.commute),
      b('08:30', '13:00', 'work', 'Block Building', C.deep),
      b('13:00', '14:00', 'food', 'OMAD Lunch', C.food),
      b('14:00', '18:00', 'work', 'Meetings', C.meet),
      b('18:00', '20:00', 'health', 'Sauna + Ice Bath', C.health),
      b('20:00', '22:00', 'mind', 'Journal & Read', C.mind),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_branson',
    name: "Richard Branson",
    category: 'Tech & CEOs',
    color: '#DC2626',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '06:30', 'health', 'Tennis or Kitesurf', C.health),
      b('06:30', '08:00', 'food', 'Family Breakfast', C.food),
      b('08:00', '12:30', 'work', 'Calls & Meetings', C.meet),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '17:30', 'work', 'Virgin Empire Work', C.work),
      b('17:30', '19:30', 'social', 'People Time', C.social),
      b('19:30', '22:00', 'family', 'Dinner & Family', C.family),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_pichai',
    name: "Sundar Pichai",
    category: 'Tech & CEOs',
    color: '#4285F4',
    blocks: [
      b('00:00', '06:30', 'sleep', 'Sleep', C.sleep),
      b('06:30', '07:30', 'family', 'Tea & News with Family', C.family),
      b('07:30', '08:30', 'food', 'Breakfast', C.food),
      b('08:30', '12:30', 'work', 'Google Sync', C.meet),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '18:30', 'work', 'Strategy & Reviews', C.work),
      b('18:30', '20:30', 'family', 'Family Dinner', C.family),
      b('20:30', '23:00', 'mind', 'Reading & Email', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_nadella',
    name: "Satya Nadella",
    category: 'Tech & CEOs',
    color: '#0078D4',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'health', 'Cricket / Run', C.health),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '12:00', 'work', 'Microsoft Sync', C.meet),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '18:00', 'work', '1:1s & Strategy', C.deep),
      b('18:00', '20:00', 'family', 'Family Time', C.family),
      b('20:00', '22:30', 'mind', 'Poetry & Reading', C.read),
      b('22:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_huffington',
    name: "Arianna Huffington",
    category: 'Tech & CEOs',
    color: '#EC4899',
    blocks: [
      b('00:00', '08:00', 'sleep', 'Sleep (8h Sacred)', C.sleep),
      b('08:00', '09:00', 'mind', 'Meditation & Yoga', C.mind),
      b('09:00', '10:00', 'food', 'Breakfast', C.food),
      b('10:00', '13:00', 'work', 'Thrive Work', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '18:00', 'work', 'Meetings', C.meet),
      b('18:00', '20:00', 'social', 'Dinner with Friends', C.social),
      b('20:00', '21:30', 'mind', 'Bath, No Devices', C.mind),
      b('21:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_sandberg',
    name: "Sheryl Sandberg",
    category: 'Tech & CEOs',
    color: '#F472B6',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'family', 'Get Kids Ready', C.family),
      b('07:00', '08:00', 'food', 'Family Breakfast', C.food),
      b('08:00', '12:00', 'work', 'Meta Meetings', C.meet),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:30', 'work', 'Strategy', C.work),
      b('17:30', '20:30', 'family', 'Home for Dinner (Hard 5:30 Stop)', C.family),
      b('20:30', '23:00', 'work', 'Late Email', C.work),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_mayer',
    name: "Marissa Mayer",
    category: 'Tech & CEOs',
    color: '#7C3AED',
    blocks: [
      b('00:00', '04:00', 'work', 'Late Work Sprint', C.work),
      b('04:00', '08:00', 'sleep', 'Short Sleep', C.sleep),
      b('08:00', '09:00', 'food', 'Breakfast', C.food),
      b('09:00', '13:00', 'work', 'Meetings (60+/wk)', C.meet),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '20:00', 'work', 'More Meetings', C.meet),
      b('20:00', '22:00', 'family', 'Family', C.family),
      b('22:00', '00:00', 'work', 'Email & Decisions', C.work),
    ],
  },
  {
    id: 'famous_nooyi',
    name: "Indra Nooyi",
    category: 'Tech & CEOs',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '04:00', 'sleep', 'Sleep', C.sleep),
      b('04:00', '07:00', 'work', 'Email & Reading', C.work),
      b('07:00', '08:00', 'family', 'Family Breakfast', C.family),
      b('08:00', '12:30', 'work', 'Office Meetings', C.meet),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '19:00', 'work', 'PepsiCo Strategy', C.work),
      b('19:00', '21:00', 'family', 'Family Dinner', C.family),
      b('21:00', '00:00', 'work', 'Letters & Reading', C.work),
    ],
  },
  {
    id: 'famous_buffett',
    name: "Warren Buffett",
    category: 'Tech & CEOs',
    color: '#F59E0B',
    blocks: [
      b('00:00', '06:45', 'sleep', 'Sleep', C.sleep),
      b('06:45', '08:00', 'mind', 'Newspapers (5)', C.read),
      b('08:00', '08:30', 'commuting_travel', 'Drive to Office (McD\'s)', C.commute),
      b('08:30', '12:00', 'mind', '500 Pages: Reports', C.read),
      b('12:00', '12:30', 'food', 'Lunch', C.food),
      b('12:30', '17:00', 'mind', 'More Reading', C.read),
      b('17:00', '18:00', 'commuting_travel', 'Drive Home', C.commute),
      b('18:00', '21:30', 'family', 'Bridge & Family', C.family),
      b('21:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_cuban',
    name: "Mark Cuban",
    category: 'Tech & CEOs',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:30', 'health', 'Cardio (1hr+)', C.health),
      b('08:30', '09:00', 'food', 'Breakfast', C.food),
      b('09:00', '13:00', 'work', 'Email & Deals', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '18:00', 'work', 'Calls & Investments', C.meet),
      b('18:00', '21:00', 'family', 'Family / Mavs Game', C.family),
      b('21:00', '23:00', 'mind', 'Reading', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },

  // ─────────────────────────── WRITERS / ARTISTS ───────────────────────────
  {
    id: 'famous_angelou',
    name: "Maya Angelou",
    category: 'Writers & Artists',
    color: '#7C3AED',
    blocks: [
      b('00:00', '05:30', 'sleep', 'Sleep', C.sleep),
      b('05:30', '06:30', 'food', 'Coffee with Husband', C.food),
      b('06:30', '07:00', 'commuting_travel', 'Drive to Hotel Room', C.commute),
      b('07:00', '14:00', 'mind', 'Hotel Room Writing', C.write),
      b('14:00', '15:00', 'food', 'Lunch', C.food),
      b('15:00', '18:00', 'mind', 'Edit & Read', C.mind),
      b('18:00', '20:00', 'family', 'Family Dinner', C.family),
      b('20:00', '23:00', 'social', 'Conversation', C.social),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_king',
    name: "Stephen King",
    category: 'Writers & Artists',
    color: '#B91C1C',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '13:30', 'mind', '2000 Words / Day', C.write),
      b('13:30', '14:30', 'food', 'Lunch', C.food),
      b('14:30', '17:00', 'downtime', 'Nap & Letters', C.down),
      b('17:00', '19:00', 'health', 'Walk', C.health),
      b('19:00', '21:00', 'family', 'Dinner', C.family),
      b('21:00', '23:00', 'mind', 'Reading', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_murakami',
    name: "Haruki Murakami",
    category: 'Writers & Artists',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '04:00', 'sleep', 'Sleep', C.sleep),
      b('04:00', '10:00', 'mind', 'Morning Writing (5-6h)', C.write),
      b('10:00', '11:00', 'food', 'Breakfast', C.food),
      b('11:00', '13:00', 'health', '10K Run', C.health),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '18:00', 'mind', 'Translation / Edit', C.mind),
      b('18:00', '19:00', 'food', 'Dinner', C.food),
      b('19:00', '21:00', 'fun_hobby', 'Music / Reading', C.fun),
      b('21:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_picasso',
    name: "Pablo Picasso",
    category: 'Writers & Artists',
    color: '#F472B6',
    blocks: [
      b('00:00', '11:00', 'sleep', 'Sleep In', C.sleep),
      b('11:00', '14:00', 'food', 'Breakfast & Friends', C.food),
      b('14:00', '15:00', 'food', 'Lunch', C.food),
      b('15:00', '21:00', 'fun_hobby', 'Studio (Long Session)', C.create),
      b('21:00', '23:00', 'food', 'Dinner', C.food),
      b('23:00', '00:00', 'fun_hobby', 'Late Studio', C.create),
    ],
  },
  {
    id: 'famous_lynch',
    name: "David Lynch",
    category: 'Writers & Artists',
    color: '#DC2626',
    blocks: [
      b('00:00', '06:30', 'sleep', 'Sleep', C.sleep),
      b('06:30', '07:00', 'mind', 'TM Meditation', C.mind),
      b('07:00', '07:30', 'food', 'Coffee & Sugar', C.food),
      b('07:30', '12:00', 'fun_hobby', 'Studio Work', C.create),
      b('12:00', '13:00', 'food', "Bob's Big Boy Lunch", C.food),
      b('13:00', '17:00', 'fun_hobby', 'Studio Continued', C.create),
      b('17:00', '17:30', 'mind', 'TM Meditation', C.mind),
      b('17:30', '20:00', 'work', 'Writing & Phone', C.work),
      b('20:00', '21:30', 'food', 'Dinner', C.food),
      b('21:30', '00:00', 'fun_hobby', 'Music & Films', C.fun),
    ],
  },
  {
    id: 'famous_warhol',
    name: "Andy Warhol",
    category: 'Writers & Artists',
    color: '#F59E0B',
    blocks: [
      b('00:00', '08:00', 'sleep', 'Sleep', C.sleep),
      b('08:00', '09:00', 'mind', 'Phone with Pat', C.mind),
      b('09:00', '11:00', 'food', 'Breakfast & Walk', C.food),
      b('11:00', '14:00', 'fun_hobby', 'Factory Work', C.create),
      b('14:00', '15:00', 'food', 'Lunch', C.food),
      b('15:00', '19:00', 'fun_hobby', 'Portraits & Tape', C.create),
      b('19:00', '23:00', 'social', 'Studio 54 / Parties', C.social),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },

  // ─────────────────────────── ATHLETES ───────────────────────────
  {
    id: 'famous_kobe',
    name: "Kobe Bryant (Mamba)",
    category: 'Athletes',
    color: '#FACC15',
    blocks: [
      b('00:00', '03:30', 'sleep', 'Sleep', C.sleep),
      b('03:30', '06:00', 'health', 'Cardio + Weights', C.health),
      b('06:00', '07:00', 'food', 'Breakfast', C.food),
      b('07:00', '11:00', 'health', 'Skill Work + Film', C.health),
      b('11:00', '12:00', 'food', 'Lunch', C.food),
      b('12:00', '14:30', 'health', 'Practice', C.health),
      b('14:30', '16:00', 'downtime', 'Recovery / Nap', C.down),
      b('16:00', '19:00', 'health', 'Shootaround / Game Prep', C.health),
      b('19:00', '22:00', 'family', 'Family Dinner', C.family),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_lebron',
    name: "LeBron James",
    category: 'Athletes',
    color: '#A855F7',
    blocks: [
      b('00:00', '08:30', 'sleep', 'Sleep (8-9h Sacred)', C.sleep),
      b('08:30', '09:30', 'food', 'Breakfast', C.food),
      b('09:30', '12:00', 'health', 'Gym & Skill Work', C.health),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '15:00', 'downtime', 'Nap', C.down),
      b('15:00', '18:00', 'health', 'Practice / Recovery', C.health),
      b('18:00', '21:00', 'family', 'Family Time', C.family),
      b('21:00', '23:30', 'fun_hobby', 'Film / Business', C.fun),
      b('23:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_brady',
    name: "Tom Brady (TB12)",
    category: 'Athletes',
    color: '#1E40AF',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '06:30', 'food', 'Smoothie', C.food),
      b('06:30', '08:00', 'health', 'Pliability + Bands', C.health),
      b('08:00', '11:00', 'health', 'Practice', C.health),
      b('11:00', '12:00', 'food', 'Lunch', C.food),
      b('12:00', '15:00', 'mind', 'Film & Study', C.mind),
      b('15:00', '17:00', 'health', 'Lift / Recovery', C.health),
      b('17:00', '18:30', 'food', 'Dinner (TB12 Diet)', C.food),
      b('18:30', '20:30', 'family', 'Family', C.family),
      b('20:30', '00:00', 'sleep', 'Sleep (8.5h)', C.sleep),
    ],
  },
  {
    id: 'famous_ronaldo',
    name: "Cristiano Ronaldo",
    category: 'Athletes',
    color: '#DC2626',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep Block 1', C.sleep),
      b('05:00', '05:30', 'food', 'Snack', C.food),
      b('05:30', '08:00', 'sleep', 'Sleep Block 2', C.sleep),
      b('08:00', '09:00', 'food', 'Breakfast', C.food),
      b('09:00', '11:30', 'health', 'Training', C.health),
      b('11:30', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '14:30', 'downtime', 'Nap', C.down),
      b('14:30', '17:00', 'health', 'Second Training', C.health),
      b('17:00', '18:00', 'food', 'Snack', C.food),
      b('18:00', '20:00', 'family', 'Family Time', C.family),
      b('20:00', '21:30', 'food', 'Dinner', C.food),
      b('21:30', '00:00', 'sleep', 'Sleep Block 3', C.sleep),
    ],
  },
  {
    id: 'famous_phelps',
    name: "Michael Phelps",
    category: 'Athletes',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '07:00', 'health', 'Pool 1 (5 mi swim)', C.health),
      b('07:00', '08:00', 'food', '12,000 cal Breakfast', C.food),
      b('08:00', '10:00', 'health', 'Dryland Training', C.health),
      b('10:00', '12:00', 'downtime', 'Recovery / Nap', C.down),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'health', 'Pool 2', C.health),
      b('17:00', '19:00', 'food', 'Dinner', C.food),
      b('19:00', '21:00', 'family', 'Family / Video Games', C.fun),
      b('21:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_serena',
    name: "Serena Williams",
    category: 'Athletes',
    color: '#10B981',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '10:00', 'health', 'On-Court Practice', C.health),
      b('10:00', '11:00', 'health', 'Recovery', C.health),
      b('11:00', '13:00', 'family', 'Family / Olympia', C.family),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '16:00', 'health', 'Gym Work', C.health),
      b('16:00', '18:00', 'work', 'Business / Serena Ventures', C.work),
      b('18:00', '20:00', 'family', 'Family Dinner', C.family),
      b('20:00', '23:00', 'fun_hobby', 'Wind Down', C.fun),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_jocko',
    name: "Jocko Willink (Discipline)",
    category: 'Athletes',
    color: '#1F2937',
    blocks: [
      b('00:00', '04:30', 'sleep', 'Sleep', C.sleep),
      b('04:30', '06:00', 'health', 'Lift', C.health),
      b('06:00', '06:30', 'food', 'Coffee & Plan', C.food),
      b('06:30', '12:00', 'work', 'Deep Work', C.deep),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'work', 'Calls & Echelon Front', C.work),
      b('17:00', '18:30', 'health', 'Jiu-Jitsu', C.health),
      b('18:30', '20:00', 'family', 'Family Dinner', C.family),
      b('20:00', '22:30', 'mind', 'Reading', C.read),
      b('22:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_wahlberg',
    name: "Mark Wahlberg",
    category: 'Athletes',
    color: '#B91C1C',
    blocks: [
      b('00:00', '02:30', 'sleep', 'Sleep', C.sleep),
      b('02:30', '03:15', 'mind', 'Prayer', C.mind),
      b('03:15', '03:45', 'food', 'Breakfast', C.food),
      b('03:45', '05:30', 'health', 'Workout 1', C.health),
      b('05:30', '08:00', 'family', 'Family Time / School', C.family),
      b('08:00', '11:00', 'work', 'Meetings', C.work),
      b('11:00', '11:30', 'food', 'Snack', C.food),
      b('11:30', '13:00', 'health', 'Golf', C.health),
      b('13:00', '14:30', 'food', 'Lunch', C.food),
      b('14:30', '16:00', 'health', 'Workout 2', C.health),
      b('16:00', '17:30', 'health', 'Cryotherapy & Recovery', C.health),
      b('17:30', '19:00', 'family', 'Dinner', C.family),
      b('19:00', '19:30', 'mind', 'Prayer', C.mind),
      b('19:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_rock',
    name: "Dwayne 'The Rock' Johnson",
    category: 'Athletes',
    color: '#0F172A',
    blocks: [
      b('00:00', '04:00', 'sleep', 'Sleep', C.sleep),
      b('04:00', '04:30', 'food', 'Pre-Workout Meal', C.food),
      b('04:30', '06:30', 'health', 'Cardio + Lift', C.health),
      b('06:30', '07:30', 'food', 'Big Breakfast', C.food),
      b('07:30', '12:00', 'work', 'On Set / Calls', C.work),
      b('12:00', '13:00', 'food', 'Lunch (Meal 4)', C.food),
      b('13:00', '17:00', 'work', 'Filming', C.work),
      b('17:00', '18:00', 'food', 'Meal 5', C.food),
      b('18:00', '20:00', 'family', 'Family', C.family),
      b('20:00', '21:00', 'food', 'Final Meal', C.food),
      b('21:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },

  // ─────────────────────────── MODERN INFLUENCE ───────────────────────────
  {
    id: 'famous_oprah',
    name: "Oprah Winfrey",
    category: 'Modern',
    color: '#7C3AED',
    blocks: [
      b('00:00', '06:20', 'sleep', 'Sleep', C.sleep),
      b('06:20', '06:40', 'mind', 'Meditation', C.mind),
      b('06:40', '08:00', 'health', 'Walk + Workout', C.health),
      b('08:00', '09:00', 'food', 'Breakfast', C.food),
      b('09:00', '13:00', 'work', 'Production Meetings', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '18:00', 'work', 'Interviews & Calls', C.meet),
      b('18:00', '20:00', 'food', 'Dinner', C.food),
      b('20:00', '22:00', 'mind', 'Reading', C.read),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_obama',
    name: "Barack Obama",
    category: 'Modern',
    color: '#1E40AF',
    blocks: [
      b('00:00', '01:00', 'work', 'Late Reading', C.work),
      b('01:00', '06:45', 'sleep', 'Sleep', C.sleep),
      b('06:45', '07:45', 'health', 'Workout', C.health),
      b('07:45', '08:45', 'food', 'Breakfast & PDB', C.food),
      b('08:45', '12:30', 'work', 'Briefings & Cabinet', C.meet),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '18:00', 'work', 'Meetings & Decisions', C.work),
      b('18:00', '19:30', 'family', 'Family Dinner', C.family),
      b('19:30', '23:30', 'mind', 'Memos & Reading', C.read),
      b('23:30', '00:00', 'work', 'Late Work', C.work),
    ],
  },
  {
    id: 'famous_michelle',
    name: "Michelle Obama",
    category: 'Modern',
    color: '#EC4899',
    blocks: [
      b('00:00', '04:30', 'sleep', 'Sleep', C.sleep),
      b('04:30', '06:00', 'health', 'Workout', C.health),
      b('06:00', '07:30', 'family', 'Family Breakfast', C.family),
      b('07:30', '09:00', 'food', 'Breakfast & Plan', C.food),
      b('09:00', '13:00', 'work', 'Foundation Work', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '17:30', 'work', 'Calls & Initiatives', C.meet),
      b('17:30', '20:00', 'family', 'Family Dinner', C.family),
      b('20:00', '22:00', 'mind', 'Reading', C.read),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_wintour',
    name: "Anna Wintour",
    category: 'Modern',
    color: '#0F172A',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '06:00', 'health', 'Tennis', C.health),
      b('06:00', '07:00', 'mind', 'Hair & Plan', C.mind),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '12:30', 'work', 'Vogue Office', C.work),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '18:00', 'work', 'Meetings', C.meet),
      b('18:00', '20:30', 'social', 'Events / Dinner', C.social),
      b('20:30', '22:00', 'work', 'Email at Home', C.work),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_reese',
    name: "Reese Witherspoon",
    category: 'Modern',
    color: '#F472B6',
    blocks: [
      b('00:00', '05:30', 'sleep', 'Sleep', C.sleep),
      b('05:30', '06:30', 'health', 'Workout', C.health),
      b('06:30', '07:30', 'family', 'Get Kids Up', C.family),
      b('07:30', '08:30', 'food', 'Breakfast', C.food),
      b('08:30', '12:30', 'work', 'Hello Sunshine', C.work),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '17:00', 'work', 'Meetings & Reading', C.meet),
      b('17:00', '20:00', 'family', 'Family Dinner', C.family),
      b('20:00', '22:00', 'mind', 'Book Club Reading', C.read),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_kim',
    name: "Kim Kardashian",
    category: 'Modern',
    color: '#A855F7',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:30', 'health', 'Workout', C.health),
      b('07:30', '09:00', 'family', 'Kids School Run', C.family),
      b('09:00', '10:00', 'food', 'Breakfast', C.food),
      b('10:00', '13:00', 'work', 'SKIMS / Beauty', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '17:00', 'work', 'Photo Shoots / Calls', C.meet),
      b('17:00', '20:00', 'family', 'Kids Dinner', C.family),
      b('20:00', '23:00', 'work', 'Law Study / Email', C.work),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_garyvee',
    name: "Gary Vaynerchuk",
    category: 'Modern',
    color: '#DC2626',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'work', 'Email & Content', C.work),
      b('07:00', '08:00', 'food', 'Breakfast & Drive', C.food),
      b('08:00', '12:30', 'work', 'Back-to-Back Meetings', C.meet),
      b('12:30', '13:30', 'food', 'Lunch (in motion)', C.food),
      b('13:30', '19:00', 'work', 'Speaking & Filming', C.meet),
      b('19:00', '20:30', 'family', 'Family', C.family),
      b('20:30', '23:00', 'social', 'Sports / Hustle', C.social),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_ferriss',
    name: "Tim Ferriss",
    category: 'Modern',
    color: '#10B981',
    blocks: [
      b('00:00', '07:30', 'sleep', 'Sleep', C.sleep),
      b('07:30', '08:00', 'mind', 'Journal & Tea', C.mind),
      b('08:00', '08:30', 'health', '5-Min Mobility', C.health),
      b('08:30', '12:30', 'work', 'Deep Work', C.deep),
      b('12:30', '13:30', 'food', 'Slow-Carb Lunch', C.food),
      b('13:30', '15:00', 'work', 'Podcast Recording', C.meet),
      b('15:00', '17:00', 'health', 'Workout', C.health),
      b('17:00', '19:00', 'work', 'Email Batching', C.work),
      b('19:00', '21:00', 'food', 'Dinner with Friends', C.food),
      b('21:00', '23:30', 'mind', 'Reading', C.read),
      b('23:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'famous_newport',
    name: "Cal Newport (Deep Work)",
    category: 'Modern',
    color: '#1E40AF',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'family', 'Breakfast with Kids', C.family),
      b('08:00', '11:30', 'work', 'Deep Work Block 1', C.deep),
      b('11:30', '12:30', 'food', 'Lunch', C.food),
      b('12:30', '15:30', 'work', 'Deep Work Block 2', C.deep),
      b('15:30', '17:00', 'work', 'Shallow Tasks Batch', C.work),
      b('17:00', '17:30', 'mind', 'Shutdown Ritual', C.mind),
      b('17:30', '20:00', 'family', 'Family Time', C.family),
      b('20:00', '22:00', 'mind', 'Reading', C.read),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },

  // ─────────────────────────── PRODUCTIVITY METHODS ───────────────────────────
  {
    id: 'system_pomodoro',
    name: "Pomodoro Workday",
    category: 'Productivity Systems',
    color: '#DC2626',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'food', 'Breakfast & Prep', C.food),
      b('08:00', '09:55', 'work', 'Pomos 1-4 (25/5)', C.work),
      b('09:55', '10:25', 'downtime', 'Long Break', C.down),
      b('10:25', '12:20', 'work', 'Pomos 5-8', C.work),
      b('12:20', '13:20', 'food', 'Lunch', C.food),
      b('13:20', '15:15', 'work', 'Pomos 9-12', C.work),
      b('15:15', '15:45', 'downtime', 'Long Break', C.down),
      b('15:45', '17:40', 'work', 'Pomos 13-16', C.work),
      b('17:40', '19:00', 'health', 'Walk / Workout', C.health),
      b('19:00', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '23:00', 'fun_hobby', 'Free Time', C.fun),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_deep_work',
    name: "Deep Work (90-min)",
    category: 'Productivity Systems',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '06:30', 'sleep', 'Sleep', C.sleep),
      b('06:30', '07:30', 'health', 'Morning Routine', C.health),
      b('07:30', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '09:30', 'work', 'Deep Block 1', C.deep),
      b('09:30', '10:00', 'downtime', 'Recovery', C.down),
      b('10:00', '11:30', 'work', 'Deep Block 2', C.deep),
      b('11:30', '12:30', 'food', 'Lunch', C.food),
      b('12:30', '14:00', 'work', 'Deep Block 3', C.deep),
      b('14:00', '14:30', 'downtime', 'Recovery', C.down),
      b('14:30', '16:00', 'work', 'Meetings (Bundled)', C.meet),
      b('16:00', '17:00', 'work', 'Shallow Email', C.work),
      b('17:00', '19:00', 'health', 'Walk + Workout', C.health),
      b('19:00', '21:00', 'family', 'Dinner', C.family),
      b('21:00', '23:00', 'mind', 'Reading', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_4hww',
    name: "4-Hour Workweek",
    category: 'Productivity Systems',
    color: '#10B981',
    blocks: [
      b('00:00', '08:00', 'sleep', 'Sleep', C.sleep),
      b('08:00', '09:00', 'health', 'Morning Movement', C.health),
      b('09:00', '10:00', 'food', 'Slow Breakfast', C.food),
      b('10:00', '12:00', 'work', 'Highest-Leverage Work', C.deep),
      b('12:00', '13:00', 'work', 'Email (Once)', C.work),
      b('13:00', '14:30', 'food', 'Lunch', C.food),
      b('14:30', '15:30', 'work', 'Vital Few Tasks', C.work),
      b('15:30', '19:00', 'fun_hobby', 'Hobby / Travel', C.fun),
      b('19:00', '21:00', 'social', 'Dinner with People', C.social),
      b('21:00', '23:00', 'mind', 'Reading', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_monk_mode',
    name: "Monk Mode",
    category: 'Productivity Systems',
    color: '#1F2937',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '06:00', 'mind', 'Meditation & Journal', C.mind),
      b('06:00', '07:00', 'health', 'Cold Plunge + Workout', C.health),
      b('07:00', '07:30', 'food', 'Breakfast', C.food),
      b('07:30', '12:00', 'work', 'Deep Work (No Phone)', C.deep),
      b('12:00', '12:30', 'food', 'Lunch', C.food),
      b('12:30', '17:00', 'work', 'Deep Work', C.deep),
      b('17:00', '18:00', 'health', 'Walk', C.health),
      b('18:00', '19:00', 'food', 'Dinner', C.food),
      b('19:00', '21:00', 'mind', 'Read & Reflect', C.read),
      b('21:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_eisenhower',
    name: "Eisenhower Day",
    category: 'Productivity Systems',
    color: '#B91C1C',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'mind', 'Plan: Quadrants', C.mind),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '11:00', 'work', 'Q1 Urgent + Important', C.deep),
      b('11:00', '13:00', 'work', 'Q2 Important Not Urgent', C.work),
      b('13:00', '14:00', 'food', 'Lunch', C.food),
      b('14:00', '16:00', 'work', 'Q2 Continued', C.work),
      b('16:00', '17:30', 'work', 'Q3 Delegate / Urgent', C.meet),
      b('17:30', '18:00', 'mind', 'Daily Review', C.mind),
      b('18:00', '20:00', 'family', 'Dinner', C.family),
      b('20:00', '22:30', 'fun_hobby', 'Free Time', C.fun),
      b('22:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_buffett_525',
    name: "Buffett's 5/25 Day",
    category: 'Productivity Systems',
    color: '#F59E0B',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'mind', 'Pick Top 5 of 25 Goals', C.mind),
      b('08:00', '08:30', 'food', 'Breakfast', C.food),
      b('08:30', '12:30', 'work', 'Goal #1 Focus', C.deep),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '17:00', 'work', 'Goals #2-3 Focus', C.deep),
      b('17:00', '18:00', 'work', 'Avoid the Avoid List', C.work),
      b('18:00', '20:00', 'family', 'Dinner', C.family),
      b('20:00', '22:30', 'mind', 'Reading', C.read),
      b('22:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_ultradian',
    name: "Ultradian Rhythm (90/20)",
    category: 'Productivity Systems',
    color: '#7C3AED',
    blocks: [
      b('00:00', '06:30', 'sleep', 'Sleep', C.sleep),
      b('06:30', '07:30', 'health', 'Morning Movement', C.health),
      b('07:30', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '09:30', 'work', '90-min Cycle 1', C.deep),
      b('09:30', '09:50', 'downtime', '20-min Recovery', C.down),
      b('09:50', '11:20', 'work', '90-min Cycle 2', C.deep),
      b('11:20', '11:40', 'downtime', 'Recovery', C.down),
      b('11:40', '13:10', 'work', 'Cycle 3', C.deep),
      b('13:10', '14:10', 'food', 'Lunch', C.food),
      b('14:10', '15:40', 'work', 'Cycle 4', C.work),
      b('15:40', '16:00', 'downtime', 'Recovery', C.down),
      b('16:00', '17:30', 'work', 'Cycle 5', C.work),
      b('17:30', '19:30', 'health', 'Walk + Workout', C.health),
      b('19:30', '21:30', 'family', 'Dinner', C.family),
      b('21:30', '23:00', 'mind', 'Reading', C.read),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_dhh_basecamp',
    name: "Basecamp 8-hour Day",
    category: 'Productivity Systems',
    color: '#3B82F6',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'family', 'Family Breakfast', C.family),
      b('08:00', '12:00', 'work', 'Solo Work', C.deep),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'work', 'Async + Solo Work', C.work),
      b('17:00', '17:30', 'mind', 'Shutdown', C.mind),
      b('17:30', '19:00', 'health', 'Workout / Walk', C.health),
      b('19:00', '21:00', 'family', 'Dinner', C.family),
      b('21:00', '23:00', 'fun_hobby', 'Free Time', C.fun),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_night_owl',
    name: "Night Owl Creator",
    category: 'Productivity Systems',
    color: '#7C3AED',
    blocks: [
      b('00:00', '04:00', 'fun_hobby', 'Late Night Creating', C.create),
      b('04:00', '11:00', 'sleep', 'Sleep In', C.sleep),
      b('11:00', '12:00', 'food', 'Brunch', C.food),
      b('12:00', '13:00', 'health', 'Light Movement', C.health),
      b('13:00', '15:00', 'work', 'Email & Admin', C.work),
      b('15:00', '18:00', 'work', 'Meetings', C.meet),
      b('18:00', '20:00', 'food', 'Dinner', C.food),
      b('20:00', '23:00', 'work', 'Deep Creative Block', C.deep),
      b('23:00', '00:00', 'fun_hobby', 'Continue Creating', C.create),
    ],
  },
  {
    id: 'system_early_bird',
    name: "5 AM Club",
    category: 'Productivity Systems',
    color: '#F59E0B',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '05:20', 'health', '20-Min Workout', C.health),
      b('05:20', '05:40', 'mind', '20-Min Reflect', C.mind),
      b('05:40', '06:00', 'mind', '20-Min Learn', C.read),
      b('06:00', '07:00', 'food', 'Breakfast', C.food),
      b('07:00', '12:00', 'work', 'Best 5 Hours', C.deep),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '17:00', 'work', 'Work', C.work),
      b('17:00', '19:00', 'family', 'Family Time', C.family),
      b('19:00', '20:30', 'food', 'Dinner', C.food),
      b('20:30', '22:00', 'mind', 'Reading', C.read),
      b('22:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'system_biphasic',
    name: "Biphasic Sleep",
    category: 'Productivity Systems',
    color: '#6B46C1',
    blocks: [
      b('00:00', '06:00', 'sleep', 'Core Sleep', C.sleep),
      b('06:00', '07:00', 'health', 'Morning Movement', C.health),
      b('07:00', '08:00', 'food', 'Breakfast', C.food),
      b('08:00', '13:00', 'work', 'Morning Work', C.work),
      b('13:00', '14:30', 'food', 'Lunch', C.food),
      b('14:30', '16:00', 'sleep', 'Siesta', C.sleep),
      b('16:00', '20:00', 'work', 'Afternoon Work', C.work),
      b('20:00', '22:00', 'food', 'Dinner', C.food),
      b('22:00', '00:00', 'fun_hobby', 'Wind Down', C.fun),
    ],
  },
  {
    id: 'system_uberman',
    name: "Polyphasic (Uberman)",
    category: 'Productivity Systems',
    color: '#8B5CF6',
    blocks: [
      b('00:00', '00:20', 'sleep', 'Nap 1', C.sleep),
      b('00:20', '04:00', 'fun_hobby', 'Late Creative', C.create),
      b('04:00', '04:20', 'sleep', 'Nap 2', C.sleep),
      b('04:20', '08:00', 'work', 'Morning Work', C.work),
      b('08:00', '08:20', 'sleep', 'Nap 3', C.sleep),
      b('08:20', '08:50', 'food', 'Breakfast', C.food),
      b('08:50', '12:00', 'work', 'Deep Work', C.deep),
      b('12:00', '12:20', 'sleep', 'Nap 4', C.sleep),
      b('12:20', '12:50', 'food', 'Lunch', C.food),
      b('12:50', '16:00', 'work', 'Afternoon Work', C.work),
      b('16:00', '16:20', 'sleep', 'Nap 5', C.sleep),
      b('16:20', '20:00', 'work', 'Evening Work', C.work),
      b('20:00', '20:20', 'sleep', 'Nap 6', C.sleep),
      b('20:20', '21:00', 'food', 'Dinner', C.food),
      b('21:00', '00:00', 'fun_hobby', 'Late Project', C.create),
    ],
  },

  // ─────────────────────────── STUDENT / GRIND ───────────────────────────
  {
    id: 'lifestyle_student',
    name: "College Student",
    category: 'Lifestyles',
    color: '#0EA5E9',
    blocks: [
      b('00:00', '08:00', 'sleep', 'Sleep', C.sleep),
      b('08:00', '08:30', 'food', 'Breakfast', C.food),
      b('08:30', '09:00', 'commuting_travel', 'Walk to Class', C.commute),
      b('09:00', '12:00', 'work', 'Classes', C.work),
      b('12:00', '13:00', 'food', 'Dining Hall Lunch', C.food),
      b('13:00', '15:00', 'work', 'More Classes', C.work),
      b('15:00', '17:00', 'mind', 'Library Study', C.read),
      b('17:00', '18:00', 'health', 'Gym', C.health),
      b('18:00', '19:30', 'food', 'Dinner', C.food),
      b('19:30', '22:30', 'work', 'Study / Group Work', C.work),
      b('22:30', '00:00', 'social', 'Hang with Friends', C.social),
    ],
  },
  {
    id: 'lifestyle_remote_dev',
    name: "Remote Developer",
    category: 'Lifestyles',
    color: '#14B8A6',
    blocks: [
      b('00:00', '07:00', 'sleep', 'Sleep', C.sleep),
      b('07:00', '08:00', 'health', 'Walk', C.health),
      b('08:00', '08:30', 'food', 'Breakfast', C.food),
      b('08:30', '12:00', 'work', 'Coding Sprint', C.deep),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '14:00', 'work', 'Standup + 1:1', C.meet),
      b('14:00', '17:00', 'work', 'Afternoon Code', C.deep),
      b('17:00', '17:30', 'work', 'Email Cleanup', C.work),
      b('17:30', '19:00', 'health', 'Workout', C.health),
      b('19:00', '21:00', 'family', 'Dinner', C.family),
      b('21:00', '23:00', 'fun_hobby', 'Side Project / Games', C.fun),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'lifestyle_new_parent',
    name: "New Parent",
    category: 'Lifestyles',
    color: '#F472B6',
    blocks: [
      b('00:00', '02:00', 'family', 'Night Feeding', C.family),
      b('02:00', '06:00', 'sleep', 'Sleep', C.sleep),
      b('06:00', '07:00', 'family', 'Morning Feed', C.family),
      b('07:00', '08:00', 'food', 'Breakfast (Coffee)', C.food),
      b('08:00', '11:00', 'family', 'Baby Time', C.family),
      b('11:00', '12:00', 'work', 'Email During Nap', C.work),
      b('12:00', '13:00', 'food', 'Lunch', C.food),
      b('13:00', '15:00', 'downtime', 'Nap When Baby Naps', C.down),
      b('15:00', '17:00', 'family', 'Walk with Stroller', C.family),
      b('17:00', '19:00', 'food', 'Dinner & Bath', C.food),
      b('19:00', '21:00', 'family', 'Bedtime Routine', C.family),
      b('21:00', '23:00', 'downtime', 'Quiet Time', C.down),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'lifestyle_shift_nurse',
    name: "Shift Nurse (Day)",
    category: 'Lifestyles',
    color: '#EC4899',
    blocks: [
      b('00:00', '05:00', 'sleep', 'Sleep', C.sleep),
      b('05:00', '05:30', 'food', 'Quick Breakfast', C.food),
      b('05:30', '06:30', 'commuting_travel', 'Drive to Hospital', C.commute),
      b('06:30', '07:00', 'work', 'Handoff / Charting', C.work),
      b('07:00', '12:00', 'work', 'Patient Care', C.work),
      b('12:00', '12:30', 'food', 'Quick Lunch', C.food),
      b('12:30', '19:00', 'work', 'Patient Care', C.work),
      b('19:00', '20:00', 'commuting_travel', 'Drive Home', C.commute),
      b('20:00', '21:30', 'food', 'Dinner & Decompress', C.food),
      b('21:30', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
  {
    id: 'lifestyle_freelancer',
    name: "Freelancer / Solopreneur",
    category: 'Lifestyles',
    color: '#F59E0B',
    blocks: [
      b('00:00', '07:30', 'sleep', 'Sleep', C.sleep),
      b('07:30', '08:30', 'health', 'Workout', C.health),
      b('08:30', '09:30', 'food', 'Breakfast & Plan', C.food),
      b('09:30', '12:30', 'work', 'Client Work', C.deep),
      b('12:30', '13:30', 'food', 'Lunch', C.food),
      b('13:30', '16:00', 'work', 'Client Calls', C.meet),
      b('16:00', '18:00', 'work', 'Marketing & Pipeline', C.work),
      b('18:00', '19:00', 'work', 'Invoicing & Email', C.work),
      b('19:00', '21:00', 'family', 'Dinner', C.family),
      b('21:00', '23:00', 'fun_hobby', 'Hobby / Read', C.fun),
      b('23:00', '00:00', 'sleep', 'Sleep', C.sleep),
    ],
  },
];

export default FAMOUS_WHEELS;
